import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Modal, message } from "antd";
import { userMessageForError } from "../../utils/apiError";
import { formatDdMmYyyy } from "../../utils/internalAuditView";
import AiProvenanceNote from "../AiProvenanceNote";
import { iaRequest } from "./iaApi";

const { TextArea } = Input;

export const SUMMARY_MAX = 4000;
export const OPINION_MAX = 1500;

// Keys match the backend's AI_REPORT_NARRATIVE_KEYS; the same key names the report field a section lands in.
const AI_SECTIONS = [
  { key: "executiveSummary", label: "Executive summary" },
  { key: "auditObjectives", label: "Audit objectives" },
  { key: "scopeStatement", label: "Scope statement" },
  { key: "methodology", label: "Methodology" },
  { key: "findingsDiscussion", label: "Findings discussion" },
  { key: "conclusions", label: "Conclusions" },
  { key: "recommendations", label: "Recommendations" },
];

function TextField({ id, label, value, max, onChange, disabled, readOnly, hint }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
        {label}
      </label>
      {readOnly ? (
        <p id={id} className="whitespace-pre-wrap text-sm text-[color:var(--color-text-hero)]">{value || "-"}</p>
      ) : (
        <>
          <TextArea
            id={id}
            aria-label={label}
            value={value}
            rows={5}
            maxLength={max}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
          <div className="mt-1 flex justify-between text-[11px] text-[color:var(--color-text-muted)]">
            <span>{hint}</span>
            <span data-testid={`${id}-counter`}>
              {value.length}/{max}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Human-written report text (executive summary, the auditor's own opinion) plus the AI draft review.
 * The AI draft is never report text until a Lead Advisor / Advisor accepts a section (Commandment IV); the AI
 * never drafts the opinion, so it has no accept control. Locked once the report is approved.
 */
export function ReportTextPanel({ projectId, snapshot, canWrite, getApiToken, onChanged }) {
  const savedSummary = snapshot?.view?.executiveSummaryText || "";
  const savedOpinion = snapshot?.view?.affirmationQuote || "";
  const [summary, setSummary] = useState(savedSummary);
  const [opinion, setOpinion] = useState(savedOpinion);
  const [busy, setBusy] = useState(false);

  useEffect(() => setSummary(savedSummary), [savedSummary]);
  useEffect(() => setOpinion(savedOpinion), [savedOpinion]);

  const report = snapshot?.auditReport;
  const aiDraft = report?.aiDraft;
  const locked = snapshot?.metadata?.signOffStatus === "approved";
  const editable = canWrite && !locked;
  const dirty = summary.trim() !== savedSummary || opinion.trim() !== savedOpinion;
  // A report generated before AI drafts were tracked carries AI text as if a human had accepted it.
  const predatesTracking = Boolean(report?.generatedAt) && !aiDraft;

  const send = useCallback(
    async (body, okText) => {
      setBusy(true);
      try {
        const data = await iaRequest(getApiToken, `/api/v1/projects/${projectId}/internal-audit/report-text`, {
          method: "PATCH",
          body,
        });
        if (okText) message.success(okText);
        await onChanged(data?.internalAuditPackage);
      } catch (e) {
        message.error(userMessageForError(e, "Could not save the report text."));
        setSummary(savedSummary);
        setOpinion(savedOpinion);
      } finally {
        setBusy(false);
      }
    },
    [projectId, getApiToken, onChanged, savedSummary, savedOpinion],
  );

  const save = () => {
    const body = {};
    if (summary.trim() !== savedSummary) body.executiveSummary = summary.trim();
    if (opinion.trim() !== savedOpinion) body.affirmationQuote = opinion.trim();
    void send(body, "Report text saved.");
  };

  const accept = (key, label) => {
    // Accepting is a separate call from saving text (the API refuses both for one section in one call).
    const doAccept = () => send({ acceptAiDraft: [key] }, `${label} accepted into the report.`);
    if (String(report?.[key] || "").trim()) {
      Modal.confirm({
        title: `Replace the current ${label.toLowerCase()}?`,
        content: "Accepting the AI draft overwrites the text already in the report for this section.",
        okText: "Replace with AI draft",
        onOk: doAccept,
      });
    } else {
      void doAccept();
    }
  };

  const draftSections = AI_SECTIONS.filter((s) => aiDraft?.sections?.[s.key]);
  const anyUnaccepted = draftSections.some((s) => !aiDraft?.accepted?.[s.key]);

  return (
    <div className="space-y-4 rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-6 shadow-sm" data-testid="report-text-panel">
      <h4 className="text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Report text</h4>
      {locked ? (
        <p data-testid="report-text-locked" className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-3 py-2 text-sm text-[color:var(--color-text-muted-dark)]">
          The report is approved; its text can no longer be changed.
        </p>
      ) : null}
      {predatesTracking ? (
        <p data-testid="report-predates-note" className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-3 py-2 text-sm text-[color:var(--color-text-muted-dark)]">
          Text may predate review tracking; review before circulating.
        </p>
      ) : null}

      <TextField
        id="ia-exec-summary"
        label="Executive summary"
        value={summary}
        max={SUMMARY_MAX}
        onChange={setSummary}
        disabled={busy}
        readOnly={!editable}
        hint="Plain text. Links and angle brackets are not allowed."
      />
      <TextField
        id="ia-opinion"
        label="Auditor's opinion"
        value={opinion}
        max={OPINION_MAX}
        onChange={setOpinion}
        disabled={busy}
        readOnly={!editable}
        hint="Written by the auditor. The AI never drafts this."
      />
      {editable ? (
        <div className="flex justify-end">
          <Button type="primary" disabled={!dirty || busy} onClick={save}>
            Save report text
          </Button>
        </div>
      ) : null}

      {aiDraft ? (
        <div className="space-y-3 border-t border-[color:var(--color-border)] pt-4" data-testid="ai-draft-panel">
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full border border-[color:var(--color-info-line)] bg-[color:var(--color-info-bg)] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-[color:var(--color-accent)]">
              {anyUnaccepted ? "AI draft, not yet accepted" : "AI draft, accepted"}
            </span>
            <span className="text-xs text-[color:var(--color-text-muted)]">Generated {formatDdMmYyyy(aiDraft.generatedAt) || "-"}</span>
          </div>
          <AiProvenanceNote
            provenance={aiDraft.aiProvenance}
            confidence={aiDraft.confidence}
            unavailableReason={aiDraft.confidenceUnavailableReason || undefined}
          />
          {draftSections.map(({ key, label }) => {
            const acc = aiDraft.accepted?.[key];
            return (
              <div key={key} data-ai-section={key} className="rounded-xl border border-[color:var(--color-border)] p-3">
                <div className="mb-1 text-xs font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">{label}</div>
                <p className="whitespace-pre-wrap text-sm text-[color:var(--color-text-muted-dark)]">{aiDraft.sections[key]}</p>
                <div className="mt-2 text-xs text-[color:var(--color-text-muted)]">
                  {acc ? (
                    <span data-testid={`ai-accepted-${key}`}>
                      Accepted by {acc.acceptedBy || "-"} on {formatDdMmYyyy(acc.acceptedAt) || "-"}
                    </span>
                  ) : editable ? (
                    <Button size="small" disabled={busy} onClick={() => accept(key, label)}>
                      Accept this section
                    </Button>
                  ) : (
                    <span>Not yet accepted. Not part of the report.</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default ReportTextPanel;
